import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Edit,
  Delete,
  Add
} from '@mui/icons-material';
import { accommodationFacilitiesAPI } from '../../services/api';
import browserGlobal from '../../utils/browserGlobal';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { canManageAccommodation, canDeleteAccommodation } from '../../utils/roleUtils';

const AccommodationFacilities = () => {
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const canAddEdit = canManageAccommodation(adminUser?.role);
  const canDelete = canDeleteAccommodation(adminUser?.role);

  const [facilities, setFacilities] = useState({ payload: [], totalRecord: 0 });
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [titleSearch, setTitleSearch] = useState('');
  const [debouncedTitle, setDebouncedTitle] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });

  const [currentFacility, setCurrentFacility] = useState({
    id: '',
    title_EN: '',
    title_DE: ''
  });

  const debouncedTitleRef = useRef(debouncedTitle);
  useEffect(() => {
    debouncedTitleRef.current = debouncedTitle;
  }, [debouncedTitle]);

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = (titleSearch || '').trim();
      const prev = debouncedTitleRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedTitle(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [titleSearch]);

  const fetchFacilities = useCallback(
    async (opts = {}) => {
      const isCancelled = typeof opts.isCancelled === 'function' ? opts.isCancelled : () => false;
      setIsLoading(true);
      try {
        const response = await accommodationFacilitiesAPI.getFacilities(page, rowsPerPage, debouncedTitle);

        if (isCancelled()) return;

        if (response?.data?.success) {
          let facilitiesData = [];

          if (response.data.payload) {
            if (Array.isArray(response.data.payload)) {
              facilitiesData = response.data.payload;
            } else if (typeof response.data.payload === 'object') {
              facilitiesData = [response.data.payload];
            }
          }

          setFacilities({
            payload: facilitiesData,
            totalRecord: response.data.totalRecord || facilitiesData.length,
            count: facilitiesData.length
          });
        } else {
          setFacilities({ payload: [], totalRecord: 0 });
        }
      } catch (error) {
        if (isCancelled()) return;
        console.error('Error fetching facilities:', error);
        setFacilities({ payload: [], totalRecord: 0 });
      } finally {
        if (!isCancelled()) {
          setIsLoading(false);
        }
      }
    },
    [page, rowsPerPage, debouncedTitle]
  );

  useEffect(() => {
    let cancelled = false;
    fetchFacilities({ isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
  }, [fetchFacilities]);

  const handleOpen = (facility = null) => {
    if (facility) {
      setCurrentFacility({
        ...facility
      });
      setEditMode(true);
    } else {
      setCurrentFacility({
        title_EN: '',
        title_DE: '',
      });
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
  };

  const handleChange = (e) => {
    setCurrentFacility({
      ...currentFacility,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    if (!currentFacility.title_EN || !currentFacility.title_DE) {
      showToast(t('facilities.toastFill'), 'warning');
      return;
    }

    try {
      if (editMode) {
        await accommodationFacilitiesAPI.updateFacility(currentFacility);
        showToast(t('facilities.toastUpdated'), 'success');
      } else {
        await accommodationFacilitiesAPI.createFacility(currentFacility);
        showToast(t('facilities.toastCreated'), 'success');
      }
      await fetchFacilities();
      handleClose();
    } catch (error) {
      console.error('Error saving facility:', error);
      showToast(t('facilities.toastSaveError'), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (browserGlobal.confirm(t('facilities.confirmDelete'))) {
      try {
        await accommodationFacilitiesAPI.deleteFacility(id);
        showToast(t('facilities.toastDeleted'), 'success');
        fetchFacilities();
      } catch (error) {
        console.error('Error deleting facility:', error);
        showToast(t('facilities.toastDeleteError'), 'error');
      }
    }
  };

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1.5, sm: 0 }}
      >
        <Typography variant="h4" color="primary">{t('facilities.title')}</Typography>
        {canAddEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('facilities.add')}
          </Button>
        )}
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder={t('facilities.searchPlaceholder')}
          value={titleSearch}
          onChange={(e) => setTitleSearch(e.target.value)}
          sx={{
            maxWidth: 500,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#e5e7eb',
              },
              '&:hover fieldset': {
                borderColor: '#0F3D3E',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#0F3D3E',
              },
            },
          }}
        />
      </Box>



      <TableContainer 
        component={Paper} 
        sx={{
          mt: 3,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
          borderRadius: 2,
          '& .MuiTableCell-head': {
            backgroundColor: '#ebf4f4',
            color: '#0F3D3E',
            fontWeight: 600
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: '#ebf4f4'
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('facilities.colEnglish')}</TableCell>
              <TableCell>{t('facilities.colGerman')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : facilities.payload.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  {t('facilities.empty')}
                </TableCell>
              </TableRow>
            ) : (
              facilities.payload.map((facility) => (
                <TableRow key={facility.id}>
                  <TableCell>{facility.title_EN}</TableCell>
                  <TableCell>{facility.title_DE}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpen(facility)}
                      sx={{ color: 'primary.main', mr: 1 }}
                    >
                      <Edit />
                    </IconButton>
                    {canDelete && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(facility.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={facilities.totalRecord ?? 0}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
        />
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? t('facilities.dialogEdit') : t('facilities.dialogAdd')}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('facilities.fieldTitleEn')}
                  name="title_EN"
                  value={currentFacility.title_EN}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('facilities.fieldTitleDe')}
                  name="title_DE"
                  value={currentFacility.title_DE}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              }
            }}
          >
            {editMode ? t('common.update') : t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccommodationFacilities;
